import React from 'react';
import type { RateMultiplier, ShortTurnConfig, Role } from '@/types';
import {
  getSampleRole,
  getSortedOvertimeMultipliers,
  getThreshold,
} from '@/utils/rateCalculations';
import { SurchargeScopeComparison } from '@/components/config_forms/shared/surcharge/SurchargeScopeComparison';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface SurchargeScopeParams {
  rateMultipliers: RateMultiplier[];
  shortTurnConfig: ShortTurnConfig;
  roles: Role[];
  minimumBillableHours?: number | null;
  onScopeChange?: (shortedHoursOnly: boolean) => void;
}

/* ------------------------------------------------------------------ */
/*  Builder                                                            */
/* ------------------------------------------------------------------ */

/**
 * Builds the surcharge-scope visualization element for injection into
 * the Surcharge Period help sheet markdown.
 */
export function buildSurchargeScopeInjectable({
  rateMultipliers,
  shortTurnConfig,
  roles,
  onScopeChange,
}: SurchargeScopeParams): {
  element: React.ReactNode;
  injectables: Record<string, React.ReactNode>;
} {
  const defaultSampleRole = getSampleRole(roles);

  // ---- rest / violation values ----
  const req_break = Number(shortTurnConfig.requiredBreakHours) || 10;
  const violationHours = Math.max(1, Math.min(req_break - 1, Math.round(req_break * 0.25)));

  // ---- shift duration ----
  const durationBillingStructure = defaultSampleRole.billingStructure;
  const durationLadder = getSortedOvertimeMultipliers(rateMultipliers, durationBillingStructure);

  let shiftHours: number;
  if (durationBillingStructure === 'day_rate') {
    shiftHours = Math.max(12, (durationLadder[0] ? (getThreshold(durationLadder[0], durationBillingStructure) || 12) : 12) + 2);
  } else {
    shiftHours = 10;
  }

  const currentMode: 'full_shift' | 'violating_hours' =
    shortTurnConfig.surchargeAppliesToShortedHoursOnly ? 'violating_hours' : 'full_shift';

  const element = React.createElement(SurchargeScopeComparison, {
    shiftHours,
    shortfallHours: violationHours,
    activeMode: currentMode,
    onModeChange: onScopeChange
      ? (mode: 'full_shift' | 'violating_hours') => onScopeChange(mode === 'violating_hours')
      : undefined,
  });

  return {
    element,
    injectables: { surcharge_scope: element },
  };
}
